import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiWrite } from '../lib/api'
import { useFinding } from '../lib/queries'
import { Empty, Money, Severity, Skel } from '../components/ui'
import { pct, thaiDateTime } from '../lib/format'
import { statusLabel } from './Findings'

const nextSteps: Record<string, { to: string; label: string; kind: 'primary' | 'ghost' }[]> = {
  open: [{ to: 'investigating', label: 'เริ่มสอบสวน', kind: 'primary' }, { to: 'false_positive', label: 'ตั้งเป็น false positive', kind: 'ghost' }],
  triaged: [{ to: 'investigating', label: 'เริ่มสอบสวน', kind: 'primary' }],
  investigating: [{ to: 'pending_review', label: 'ส่งทวนสอบ', kind: 'primary' }, { to: 'false_positive', label: 'ปิดเป็น false positive', kind: 'ghost' }],
  pending_review: [{ to: 'approved', label: 'อนุมัติข้อสังเกต', kind: 'primary' }, { to: 'rejected', label: 'ตีกลับ', kind: 'ghost' }],
  approved: [{ to: 'closed', label: 'ปิดเคส', kind: 'primary' }],
  rejected: [{ to: 'investigating', label: 'เปิดสอบใหม่', kind: 'primary' }],
  false_positive: [{ to: 'closed', label: 'ปิดเคส', kind: 'primary' }],
}

export function FindingDetail() {
  const { id } = useParams()
  const q = useFinding(id)
  const qc = useQueryClient()
  const navigate = useNavigate()

  const transition = useMutation({
    mutationFn: (to: string) => apiWrite(`/v1/findings/${id}`, 'PATCH', { status: to }),
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['finding', id] }); void qc.invalidateQueries({ queryKey: ['findings'] }) },
  })

  const openCase = useMutation({
    mutationFn: () => apiWrite<{ id: string }>('/v1/cases', 'POST', {
      engagement_id: q.data!.engagement_id,
      client_id: q.data!.client_id,
      title: q.data!.title,
      finding_ids: [q.data!.id],
    }),
    onSuccess: (c) => navigate(`/cases/${c.id}`),
  })

  if (q.isLoading) return <div className="stack"><Skel h={40} /><Skel h={200} /><Skel h={140} /></div>
  if (q.isError || !q.data) return <div className="card"><Empty title="ไม่พบข้อสังเกต" body="รายการนี้อาจถูกลบหรือคุณไม่มีสิทธิ์เข้าถึง" /></div>

  const f = q.data
  const steps = f.explanation?.steps ?? []
  const risk = f.explanation?.risk ?? {}
  const actions = nextSteps[f.status] ?? []

  return (
    <>
      <div className="row" style={{ marginBottom: 18 }}>
        <Link to="/findings" className="dim" style={{ fontSize: 12 }}>← กลับไปยังข้อสังเกต</Link>
        <span className="chip" style={{ marginLeft: 'auto' }}>{statusLabel(f.status)}</span>
      </div>

      <div className="card card-pad rise" style={{ padding: 28 }}>
        <div className="row" style={{ alignItems: 'flex-start', gap: 24 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="eyebrow" style={{ marginBottom: 14 }}>{f.rule_code} · กฎเวอร์ชัน {f.rule_version}</div>
            <h1 className="display-m" style={{ lineHeight: 1.22 }}>{f.title}</h1>
            <p className="muted" style={{ marginTop: 14, maxWidth: 720, lineHeight: 1.75 }}>{f.summary}</p>
          </div>
          <div style={{ textAlign: 'right', minWidth: 180 }}>
            <div className="eyebrow">มูลค่าที่เสี่ยง</div>
            <div className="display-m tnum" style={{ marginTop: 8 }}><Money satang={f.amount_at_risk_satang} /></div>
            <div style={{ marginTop: 12 }}><Severity value={f.severity} /></div>
          </div>
        </div>

        <div className="rule" style={{ margin: '24px 0' }} />

        <div className="grid-3" style={{ gap: 24 }}>
          <div>
            <div className="eyebrow">คะแนนความเสี่ยง</div>
            <div className="row" style={{ marginTop: 8, gap: 12 }}>
              <span className="display-m tnum">{f.risk_score}</span>
              <div className="bar" style={{ flex: 1, height: 4 }}>
                <i style={{ width: `${f.risk_score}%`, background: f.risk_score >= 60 ? 'var(--color-amber)' : 'var(--color-gold)' }} />
              </div>
            </div>
          </div>
          <div>
            <div className="eyebrow">ความมั่นใจของโมเดล</div>
            <div className="row" style={{ marginTop: 8, gap: 12 }}>
              <span className="display-m tnum">{pct(f.confidence)}</span>
              <div className="bar" style={{ flex: 1, height: 4 }}><i style={{ width: `${f.confidence * 100}%`, background: 'var(--color-emerald-soft)' }} /></div>
            </div>
          </div>
          <div>
            <div className="eyebrow">สร้างเมื่อ</div>
            <div style={{ marginTop: 12, fontSize: 13 }}>{thaiDateTime(f.created_at)}</div>
          </div>
        </div>

        {actions.length > 0 ? (
          <div className="row" style={{ marginTop: 26, gap: 10 }}>
            {actions.map((a) => (
              <button key={a.to} className={`btn ${a.kind === 'primary' ? 'btn-primary' : ''}`}
                disabled={transition.isPending} onClick={() => transition.mutate(a.to)}>
                {transition.isPending ? 'กำลังบันทึก…' : a.label}
              </button>
            ))}
            {f.status !== 'closed' ? (
              <button className="btn btn-ghost" disabled={openCase.isPending} onClick={() => openCase.mutate()}>
                {openCase.isPending ? 'กำลังเปิดเคส…' : 'รวมเข้าเคส'}
              </button>
            ) : null}
            {transition.isError ? <span className="dim" style={{ fontSize: 12 }}>{(transition.error as Error).message}</span> : null}
          </div>
        ) : null}
      </div>

      <div className="grid-main" style={{ marginTop: 16 }}>
        <div className="card card-pad">
          <div className="card-label">เส้นทางการคำนวณ</div>
          <div className="heading-s" style={{ marginTop: 6, marginBottom: 18 }}>ทำไมระบบจึงสร้างข้อสังเกตนี้</div>
          <div className="trace">
            {steps.map((s, i) => (
              <div key={i} className="trace-step">{s}</div>
            ))}
          </div>
          {f.explanation?.formula ? (
            <div style={{ marginTop: 20, padding: 14, border: '1px solid var(--color-hairline)', borderRadius: 'var(--radius-md)', background: 'rgba(255,255,255,0.015)' }}>
              <div className="eyebrow" style={{ marginBottom: 8 }}>สูตรที่ใช้</div>
              <div className="mono" style={{ color: 'var(--color-gold)' }}>{f.explanation.formula}</div>
            </div>
          ) : null}
          {Object.keys(risk).length > 0 ? (
            <div style={{ marginTop: 18 }}>
              <div className="eyebrow" style={{ marginBottom: 10 }}>องค์ประกอบคะแนน</div>
              <dl className="kv">
                {Object.entries(risk).map(([k, v]) => (
                  <div key={k} style={{ display: 'contents' }}>
                    <dt className="mono">{k}</dt>
                    <dd className="tnum">{String(v)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : null}
        </div>

        <div className="card card-pad">
          <div className="card-label">หลักฐานอ้างอิง</div>
          <div className="heading-s" style={{ marginTop: 6 }}>{f.evidence?.length ?? 0} รายการ</div>
          <div style={{ marginTop: 8 }}>
            {(f.evidence ?? []).map((e, i) => (
              <div key={e.id} className="evidence-row">
                <span className="mono dim">{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <div style={{ color: 'var(--color-ivory-muted)' }}>{e.excerpt || e.ref_id}</div>
                  <div className="dim mono" style={{ fontSize: 10.5, marginTop: 3 }}>{e.kind} · {e.ref_id?.slice(0, 8)}</div>
                </div>
                <span className="dim">↗</span>
              </div>
            ))}
            {(f.evidence ?? []).length === 0 ? <p className="dim" style={{ fontSize: 12 }}>ไม่มีหลักฐานแนบ</p> : null}
          </div>
        </div>
      </div>
    </>
  )
}
